import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPlacemarks, goTo as goToRoute } from '@/shared/actions';
import { translate } from '@/services/TranslationService';
import { toast } from '@/components/ui/use-toast';
import { getCoordinatesForAuthority } from '../../newReport/actions';
import { getFeatureCollectionMarkers } from '../MyCityHelper';
import {
  getPOISForAuthority,
  getPlacemarksPoiZones,
  getReportsForAuthority,
  setFilters,
  subscribeToPoi,
  unsubscribeFromPoi,
} from '../actions';
import {
  DEFAULT_FILTERS,
  MIN_ZOOM,
  buildBaseCollection,
  buildGeozoneFilterGroups,
  createLoaderStyles,
  estimateZoomFromBounds,
  filterFeatureCollection,
  filterGeozoneFeatureCollection,
  getBoundsFromFeatures,
  getFitBoundsFromPlacemarks,
  isPointInsideBounds,
  selectMyCityState,
} from '../utils';

const getInitialIsMobileView = () =>
  typeof window !== 'undefined' ? window.innerWidth < 768 : false;

const POPUP_MAX_HEIGHT = 520;
const POPUP_MIN_HEIGHT = 180;
const POPUP_MAP_GUTTER = 64;

const showLocationError = (error) => {
  const permissionDenied = error?.code === 1;
  toast({
    variant: 'destructive',
    title: translate(permissionDenied ? 'LocationPermissionRequired' : 'Error'),
    description: translate(
      permissionDenied ? 'CurrentLocationPrompt' : 'OTHER.genericError'
    ),
  });
};

const getGeozonePopupLayout = (event) => {
  const clickX = event?.point?.x;
  const map = event?.target;
  const canvas = event?.target?.getCanvas?.();
  const mapWidth = canvas?.clientWidth;
  const mapHeight = canvas?.clientHeight;
  const horizontalAnchor =
    Number.isFinite(clickX) &&
    Number.isFinite(mapWidth) &&
    clickX < mapWidth / 2
      ? 'left'
      : 'right';
  const fallbackCoordinates = [event?.lngLat?.lng, event?.lngLat?.lat];
  let coordinates = fallbackCoordinates;

  if (
    Number.isFinite(clickX) &&
    Number.isFinite(mapHeight) &&
    typeof map?.unproject === 'function'
  ) {
    const centeredPosition = map.unproject([clickX, mapHeight / 2]);
    if (
      Number.isFinite(centeredPosition?.lng) &&
      Number.isFinite(centeredPosition?.lat)
    ) {
      coordinates = [centeredPosition.lng, centeredPosition.lat];
    }
  }

  return {
    anchor: horizontalAnchor,
    coordinates,
    maxHeight: Number.isFinite(mapHeight)
      ? Math.max(
          POPUP_MIN_HEIGHT,
          Math.min(POPUP_MAX_HEIGHT, mapHeight - POPUP_MAP_GUTTER)
        )
      : POPUP_MAX_HEIGHT,
  };
};

const parseJsonProperty = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

export const useMyCityData = () => {
  const dispatch = useDispatch();
  const {
    reports,
    pois,
    activeFilters,
    adoptATreeModuleEnabled,
    placemarks,
    poiZoneTypes,
    placemarksForPoiZones,
    translationsForGeozones,
    authorityName,
    countryCode,
  } = useSelector(selectMyCityState);

  const [center, setCenter] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [selectedGeozone, setSelectedGeozone] = useState(null);
  const [geozonePopupCoordinates, setGeozonePopupCoordinates] = useState(null);
  const [geozonePopupAnchor, setGeozonePopupAnchor] = useState('right');
  const [geozonePopupMaxHeight, setGeozonePopupMaxHeight] =
    useState(POPUP_MAX_HEIGHT);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [showGeozoneInfoWindow, setShowGeozoneInfoWindow] = useState(false);
  const [markersLoaded, setMarkersLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [userGpsLocation, setUserGpsLocation] = useState(null);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [authorityBounds, setAuthorityBounds] = useState(null);
  const [markerBounds, setMarkerBounds] = useState(null);
  const [isMobileView, setIsMobileView] = useState(getInitialIsMobileView);
  const zoom = useMemo(() => [MIN_ZOOM], []);
  const loaderStyles = useMemo(() => createLoaderStyles(), []);
  const fitBounds = useMemo(
    () => markerBounds || authorityBounds,
    [markerBounds, authorityBounds]
  );

  const dataRequestIdRef = useRef(0);
  const defaultCenterRef = useRef(null);
  const [defaultFitBounds, setDefaultFitBounds] = useState(null);
  const [defaultViewZoom, setDefaultViewZoom] = useState(MIN_ZOOM);
  const viewportSizeRef = useRef({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      viewportSizeRef.current = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    dispatch(setFilters(DEFAULT_FILTERS));
  }, [dispatch]);

  const baseCollection = useMemo(
    () => buildBaseCollection(reports, pois),
    [reports, pois]
  );

  const markerHtmlArray = useMemo(
    () => getFeatureCollectionMarkers(baseCollection),
    [baseCollection]
  );

  const featureCollectionPoints = useMemo(
    () => filterFeatureCollection(baseCollection, activeFilters),
    [baseCollection, activeFilters]
  );

  const featureCollection = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: featureCollectionPoints,
    }),
    [featureCollectionPoints]
  );

  const geozoneFilterGroups = useMemo(
    () => buildGeozoneFilterGroups(poiZoneTypes, placemarksForPoiZones),
    [poiZoneTypes, placemarksForPoiZones]
  );

  const geozoneFeatureCollection = useMemo(
    () => filterGeozoneFeatureCollection(placemarksForPoiZones, activeFilters),
    [placemarksForPoiZones, activeFilters]
  );

  const visibleMapFeatures = useMemo(
    () => [
      ...featureCollectionPoints,
      ...(geozoneFeatureCollection.features || []),
    ],
    [featureCollectionPoints, geozoneFeatureCollection]
  );

  useEffect(() => {
    setShowInfoWindow(false);
    setShowGeozoneInfoWindow(false);
  }, [activeFilters]);

  useEffect(() => {
    if (!authorityName) {
      return;
    }

    dataRequestIdRef.current += 1;
    const requestId = dataRequestIdRef.current;
    setMarkersLoaded(false);
    setShowInfoWindow(false);
    setShowGeozoneInfoWindow(false);
    setSelectedPoint(null);
    setSelectedGeozone(null);
    setAuthorityBounds(null);
    setMarkerBounds(null);

    const fetchData = async () => {
      try {
        const responses = await Promise.all([
          dispatch(
            getPOISForAuthority({ includeTrees: adoptATreeModuleEnabled })
          ),
          dispatch(getReportsForAuthority()),
          getCoordinatesForAuthority(authorityName, countryCode),
          dispatch(getPlacemarksPoiZones()),
        ]);

        if (dataRequestIdRef.current !== requestId) {
          return;
        }

        const coordinates = responses[2];
        const lng = parseFloat(coordinates?.longitude);
        const lat = parseFloat(coordinates?.latitude);

        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          const newCenter = [lng, lat];
          setCenter(newCenter);
          setUserLocation({ lat, lng });
          defaultCenterRef.current = newCenter;
        }

        setMarkersLoaded(true);
      } catch (error) {
        console.error('Failed to load MyCity data', error);
        if (dataRequestIdRef.current === requestId) {
          setMarkersLoaded(true);
        }
      }
    };

    fetchData();
    dispatch(getPlacemarks({ authority: authorityName }));
  }, [
    authorityName,
    countryCode,
    adoptATreeModuleEnabled,
    poiZoneTypes,
    dispatch,
  ]);

  useEffect(() => {
    if (!placemarks?.features?.length || center.length !== 2) {
      return;
    }

    const bounds = getFitBoundsFromPlacemarks(placemarks);
    if (!bounds) return;
    if (!isPointInsideBounds(bounds, center)) {
      return;
    }

    setAuthorityBounds(bounds);
    setDefaultFitBounds(bounds);
    setDefaultViewZoom(estimateZoomFromBounds(bounds, viewportSizeRef.current));
  }, [placemarks, center]);

  useEffect(() => {
    if (
      !visibleMapFeatures?.length ||
      (visibleMapFeatures.length === 1 &&
        visibleMapFeatures[0]?.geometry?.type === 'Point')
    ) {
      setMarkerBounds(null);
      return;
    }

    const bounds = getBoundsFromFeatures(visibleMapFeatures);
    if (!bounds) {
      setMarkerBounds(null);
      return;
    }

    setMarkerBounds(bounds);
    setDefaultFitBounds(bounds);
    setDefaultViewZoom(estimateZoomFromBounds(bounds, viewportSizeRef.current));
  }, [visibleMapFeatures]);

  const openInfoWindow = useCallback((event) => {
    const coordinates = event?.features?.[0]?.geometry?.coordinates?.slice();
    const rawPoint = event?.features?.[0]?.properties?.point;
    const parsedPoint =
      typeof rawPoint === 'string' ? JSON.parse(rawPoint) : rawPoint;

    if (!coordinates || !parsedPoint) {
      return;
    }

    setCenter(coordinates);
    setSelectedPoint(parsedPoint);
    setShowGeozoneInfoWindow(false);
    setSelectedGeozone(null);
    setShowInfoWindow(true);
  }, []);

  const openGeozoneInfoWindow = useCallback((event) => {
    const feature = event?.features?.[0];
    const lng = event?.lngLat?.lng;
    const lat = event?.lngLat?.lat;

    if (!feature || !Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }

    const popupLayout = getGeozonePopupLayout(event);

    setSelectedGeozone({
      ...feature,
      properties: {
        ...feature.properties,
        description: parseJsonProperty(feature.properties?.description),
        open_times: parseJsonProperty(feature.properties?.open_times),
      },
    });
    setGeozonePopupCoordinates(popupLayout.coordinates);
    setGeozonePopupAnchor(popupLayout.anchor);
    setGeozonePopupMaxHeight(popupLayout.maxHeight);
    setShowInfoWindow(false);
    setSelectedPoint(null);
    setShowGeozoneInfoWindow(true);
  }, []);

  const closeInfoWindow = useCallback(() => {
    setShowInfoWindow(false);
  }, []);

  const closeGeozoneInfoWindow = useCallback(() => {
    setShowGeozoneInfoWindow(false);
  }, []);

  const onClickCluster = useCallback((event) => {
    const { lng, lat } = event?.lngLat || {};
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    setCenter([lng, lat]);
  }, []);

  const zoomControlClick = useCallback(() => {
    if (!selectedPoint) return;
    const lng = parseFloat(selectedPoint.longitude);
    const lat = parseFloat(selectedPoint.latitude);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    setShowInfoWindow(false);
    setCenter([lng, lat]);
  }, [selectedPoint]);

  const handleFiltersChanged = useCallback(
    (selectedPointType, filterKey = 'name') => {
      if (!selectedPointType) return;

      let updatedFilters = [...activeFilters];
      const index = updatedFilters.findIndex(
        (f) => f[filterKey] === selectedPointType
      );
      const isStatus = filterKey === 'status_name';
      const hasReportsFilter = updatedFilters.some((f) => f.name === 'REPORTS');

      if (index === -1) {
        updatedFilters.push({ [filterKey]: selectedPointType });
        if (isStatus && !hasReportsFilter) {
          updatedFilters.push({ name: 'REPORTS' });
        }
      } else {
        updatedFilters.splice(index, 1);
        if (isStatus) {
          const remainingStatus = updatedFilters.some((f) => f.status_name);
          if (!remainingStatus && hasReportsFilter) {
            updatedFilters = updatedFilters.filter((f) => f.name !== 'REPORTS');
          }
        }
      }

      const hasNonReportsFilters = updatedFilters.some(
        (f) => f.name && f.name !== 'REPORTS'
      );
      const hasStatusFilters = updatedFilters.some((f) => f.status_name);
      const hasGeozoneFilters = updatedFilters.some(
        (f) => f.geozone_type && f.accessibility_level
      );

      if (!hasNonReportsFilters && !hasStatusFilters && !hasGeozoneFilters) {
        dispatch(setFilters([]));
        return;
      }

      dispatch(setFilters(updatedFilters));
    },
    [activeFilters, dispatch]
  );

  const clearFilters = useCallback(() => {
    dispatch(setFilters([]));
    setShowInfoWindow(false);
    setShowGeozoneInfoWindow(false);
    setSelectedPoint(null);
    setSelectedGeozone(null);
  }, [dispatch]);

  const handleGeozoneFiltersChanged = useCallback(
    (geozoneType, accessibilityLevel) => {
      if (!geozoneType || !accessibilityLevel) return;

      const updatedFilters = [...activeFilters];
      const index = updatedFilters.findIndex(
        (filter) =>
          filter.geozone_type === geozoneType &&
          filter.accessibility_level === accessibilityLevel
      );

      if (index === -1) {
        updatedFilters.push({
          geozone_type: geozoneType,
          accessibility_level: accessibilityLevel,
        });
      } else {
        updatedFilters.splice(index, 1);
      }

      dispatch(setFilters(updatedFilters));
    },
    [activeFilters, dispatch]
  );

  const goTo = useCallback(
    (page, urlData, reducerData) => {
      dispatch(goToRoute(page, urlData, reducerData));
    },
    [dispatch]
  );

  const handleSubscribeToPoi = useCallback(
    (poi) => dispatch(subscribeToPoi(poi)),
    [dispatch]
  );

  const handleUnsubscribeFromPoi = useCallback(
    (poi) => dispatch(unsubscribeFromPoi(poi)),
    [dispatch]
  );

  const resetView = useCallback(() => {
    if (!defaultCenterRef.current && !defaultFitBounds) {
      return;
    }

    if (defaultCenterRef.current) {
      setCenter(defaultCenterRef.current);
    }

    setSelectedPoint(null);
    setSelectedGeozone(null);
    setShowInfoWindow(false);
    setShowGeozoneInfoWindow(false);
  }, [defaultFitBounds]);

  const requestUserLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      showLocationError();
      return;
    }

    if (isRequestingLocation) {
      return;
    }

    setIsRequestingLocation(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const lat = parseFloat(coords?.latitude);
        const lng = parseFloat(coords?.longitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          setIsRequestingLocation(false);
          showLocationError();
          return;
        }

        setUserGpsLocation({ lat, lng });
        setSelectedPoint(null);
        setSelectedGeozone(null);
        setShowInfoWindow(false);
        setShowGeozoneInfoWindow(false);
        setIsRequestingLocation(false);
      },
      (error) => {
        console.error('Failed to acquire user location', error);
        setIsRequestingLocation(false);
        showLocationError(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [isRequestingLocation]);

  const mapProps = useMemo(
    () => ({
      goTo,
      isFromCity: true,
      zoom,
      center,
      subscribeToPoi: handleSubscribeToPoi,
      markerHtmlArray,
      userLocation,
      featureCollection,
      unsubscribeFromPoi: handleUnsubscribeFromPoi,
      selectedPoint: selectedPoint || {},
      showInfoWindow,
      onClickCluster,
      openInfoWindow,
      closeInfoWindow,
      geozoneFeatureCollection,
      selectedGeozone,
      geozonePopupCoordinates,
      geozonePopupAnchor,
      geozonePopupMaxHeight,
      showGeozoneInfoWindow,
      openGeozoneInfoWindow,
      closeGeozoneInfoWindow,
      zoomControlClick,
      placemarks,
      fitBounds,
      resetView,
      defaultFitBounds,
      defaultViewZoom,
      userGpsLocation,
      requestUserLocation,
      isRequestingLocation,
    }),
    [
      goTo,
      zoom,
      center,
      handleSubscribeToPoi,
      markerHtmlArray,
      userLocation,
      featureCollection,
      handleUnsubscribeFromPoi,
      selectedPoint,
      showInfoWindow,
      onClickCluster,
      openInfoWindow,
      closeInfoWindow,
      geozoneFeatureCollection,
      selectedGeozone,
      geozonePopupCoordinates,
      geozonePopupAnchor,
      geozonePopupMaxHeight,
      showGeozoneInfoWindow,
      openGeozoneInfoWindow,
      closeGeozoneInfoWindow,
      zoomControlClick,
      placemarks,
      fitBounds,
      resetView,
      defaultFitBounds,
      defaultViewZoom,
      userGpsLocation,
      requestUserLocation,
      isRequestingLocation,
    ]
  );

  const filtersProps = useMemo(
    () => ({
      reports,
      pois,
      activeFilters,
      filtersChanged: handleFiltersChanged,
      geozoneFilterGroups,
      geozoneFiltersChanged: handleGeozoneFiltersChanged,
      translationsForGeozones,
      clearFilters,
    }),
    [
      reports,
      pois,
      activeFilters,
      handleFiltersChanged,
      geozoneFilterGroups,
      handleGeozoneFiltersChanged,
      translationsForGeozones,
      clearFilters,
    ]
  );

  const resultsCount = visibleMapFeatures.length;

  return {
    isMobileView,
    markersLoaded,
    loaderStyles,
    mapProps,
    filtersProps,
    resultsCount,
    activeFilters,
    resetView,
    defaultFitBounds,
    defaultViewZoom,
    userGpsLocation,
    requestUserLocation,
    isRequestingLocation,
  };
};

export default useMyCityData;
